// src/components/modals/AdvSearchModal.jsx
// Búsqueda avanzada — equivalente a advsearch.asp / advsearchwof.asp
//
// Props:
//   show        — boolean, abre/cierra el modal
//   onClose     — función al cerrar sin buscar
//   onSearch    — función(params) al hacer clic en Buscar
//   isReceipts  — boolean: true=campos de recibos (Año/Período/CUIL), false=genérico

import React, { useState, useEffect } from 'react';
import { documentsApi, tabsApi } from '../../services/api';
import { useTexts, TXT } from '../../context/TextContext';
import { useToast } from '../../context/ToastContext';

const EMPTY = {
  titulo:      '',
  fechaDesde:  '',
  fechaHasta:  '',
  cuil:        '',
  anio:        '',
  periodo:     '',
  inclDesc:    false,
  descriptors: [],   // [{ id, name }]
};

export default function AdvSearchModal({ show, onClose, onSearch, isReceipts = false }) {
  const { t } = useTexts();
  const { showToast } = useToast();

  const [form, setForm]       = useState(EMPTY);
  const [anios, setAnios]     = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [descSearch, setDescSearch] = useState('');
  const [descOptions, setDescOptions] = useState([]);

  // Cargar opciones de Año y Período (solo para recibos)
  useEffect(() => {
    if (!show || !isReceipts) return;
    documentsApi.advSearchOptions().then(res => {
      setAnios(res.data.anios || []);
      setPeriodos(res.data.periodos || []);
    }).catch(() => {});
  }, [show, isReceipts]);

  // Limpiar al abrir
  useEffect(() => {
    if (show) { setForm(EMPTY); setDescSearch(''); setDescOptions([]); }
  }, [show]);

  // Autocomplete de descriptores
  useEffect(() => {
    if (!descSearch.trim()) { setDescOptions([]); return; }
    const timer = setTimeout(() => {
      tabsApi.searchDescriptors(descSearch)
        .then(res => setDescOptions(res.data || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [descSearch]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const addDescriptor = (desc) => {
    if (form.descriptors.find(d => d.id === desc.id)) return;
    setForm(prev => ({ ...prev, descriptors: [...prev.descriptors, desc] }));
    setDescSearch('');
    setDescOptions([]);
  };

  const removeDescriptor = (id) => {
    setForm(prev => ({ ...prev, descriptors: prev.descriptors.filter(d => d.id !== id) }));
  };

  const handleSearch = () => {
    const params = {
      search:      form.titulo || undefined,
      fechaDesde:  form.fechaDesde  || undefined,
      fechaHasta:  form.fechaHasta  || undefined,
      cuil:        form.cuil        || undefined,
      anio:        form.anio        || undefined,
      periodo:     form.periodo     || undefined,
      descriptorIds: form.inclDesc && form.descriptors.length
        ? form.descriptors.map(d => d.id)
        : undefined,
    };
    // Limpiar undefined
    Object.keys(params).forEach(k => params[k] === undefined && delete params[k]);
    onSearch(params);
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">

          {/* Header */}
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fa-solid fa-magnifying-glass me-2 color1" />
              {t(TXT.SEARCH, 'Búsqueda avanzada')}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          {/* Body */}
          <div className="modal-body">
            <div className="container-fluid">

              {/* Título / Nombre */}
              <div className="row mt-1">
                <div className="col-sm-12">
                  <div className="container">
                    <div className="row campo" style={{ borderBottom: '1px solid #ccc' }}>
                      <div className="col-sm-3 mb-2">
                        {isReceipts ? 'Apellido y Nombre' : t(TXT.TITLE_LABEL, 'Título')}
                      </div>
                      <div className="col-sm-9 mb-2 valor">
                        <input
                          type="text"
                          style={{ border: 'none', outline: 0, minWidth: '100%', background: 'transparent' }}
                          value={form.titulo}
                          onChange={e => set('titulo', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Año y Período (solo recibos) */}
              {isReceipts && (
                <>
                  <div className="row mt-1">
                    <div className="col-sm-6">
                      <div className="container">
                        <div className="row campo" style={{ borderBottom: '1px solid #ccc' }}>
                          <div className="col-sm-3 mb-2">Año</div>
                          <div className="col-sm-9 mb-2 valor">
                            <select
                              style={{ border: 'none', outline: 0, minWidth: '100%', background: 'transparent' }}
                              value={form.anio}
                              onChange={e => set('anio', e.target.value)}
                            >
                              <option value=""></option>
                              {anios.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="container">
                        <div className="row campo" style={{ borderBottom: '1px solid #ccc' }}>
                          <div className="col-sm-3 mb-2">Período</div>
                          <div className="col-sm-9 mb-2 valor">
                            <select
                              style={{ border: 'none', outline: 0, minWidth: '100%', background: 'transparent' }}
                              value={form.periodo}
                              onChange={e => set('periodo', e.target.value)}
                            >
                              <option value=""></option>
                              {periodos.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CUIL */}
                  <div className="row mt-1">
                    <div className="col-sm-6">
                      <div className="container">
                        <div className="row campo" style={{ borderBottom: '1px solid #ccc' }}>
                          <div className="col-sm-3 mb-2">CUIL</div>
                          <div className="col-sm-9 mb-2 valor">
                            <input
                              type="text"
                              style={{ border: 'none', outline: 0, minWidth: '100%', background: 'transparent' }}
                              value={form.cuil}
                              onChange={e => set('cuil', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Fecha Desde / Hasta */}
              <div className="row mt-1">
                <div className="col-sm-6">
                  <div className="container">
                    <div className="row campo" style={{ borderBottom: '1px solid #ccc' }}>
                      <div className="col-sm-5 mb-2">{t(TXT.DATE_FROM, 'Fecha desde')}</div>
                      <div className="col-sm-7 mb-2 valor">
                        <input
                          type="date"
                          style={{ border: 'none', outline: 0, minWidth: '100%', background: 'transparent' }}
                          value={form.fechaDesde}
                          onChange={e => set('fechaDesde', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="container">
                    <div className="row campo" style={{ borderBottom: '1px solid #ccc' }}>
                      <div className="col-sm-5 mb-2">{t(TXT.DATE_TO, 'Fecha hasta')}</div>
                      <div className="col-sm-7 mb-2 valor">
                        <input
                          type="date"
                          style={{ border: 'none', outline: 0, minWidth: '100%', background: 'transparent' }}
                          value={form.fechaHasta}
                          onChange={e => set('fechaHasta', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Incluir descriptores */}
              <div className="row mt-1">
                <div className="col-sm-6">
                  <div className="container">
                    <div className="row campo" style={{ borderBottom: '1px solid #ccc' }}>
                      <div className="col-sm-10 mb-2">{t(TXT.INCLUDE_KEYWORDS, 'Incluir descriptores')}</div>
                      <div className="col-sm-2 mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={form.inclDesc}
                          onChange={e => set('inclDesc', e.target.checked)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Panel descriptores (visible solo si inclDesc=true) */}
              {form.inclDesc && (
                <>
                  {/* Badges seleccionados */}
                  <div className="row mt-2">
                    <div className="col-12">
                      {form.descriptors.map(d => (
                        <span key={d.id} className="badge text-bg-secondary p-2 m-1">
                          <i
                            className="fa-solid fa-square-minus fa-1x me-1"
                            style={{ cursor: 'pointer' }}
                            onClick={() => removeDescriptor(d.id)}
                          />
                          {d.name}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Autocomplete */}
                  <div className="row mt-2 position-relative">
                    <div className="col-12 d-flex align-items-center gap-2">
                      <div className="position-relative flex-grow-1">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder={`${t(TXT.SEARCH, 'Buscar')} ${t(TXT.KEYWORDS_TAB, 'descriptor')}…`}
                          value={descSearch}
                          onChange={e => setDescSearch(e.target.value)}
                        />
                        {descOptions.length > 0 && (
                          <ul className="list-group position-absolute w-100" style={{ zIndex: 1000, top: '100%' }}>
                            {descOptions.map(d => (
                              <li
                                key={d.id}
                                className="list-group-item list-group-item-action py-1"
                                style={{ cursor: 'pointer' }}
                                onClick={() => addDescriptor(d)}
                              >
                                {d.name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button className="button color1" onClick={handleSearch}>
              <i className="fa-solid fa-check fa-1x color1" />
              &nbsp;{t(TXT.SEARCH, 'Buscar')}
            </button>
            <button className="button" onClick={onClose}>
              <i className="fa-solid fa-xmark fa-1x" />
              &nbsp;{t(TXT.CANCEL, 'Cancelar')}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
